import React, { useEffect } from "react";
import { useAppSelector } from "../../../hooks/hooks";
import {
  selectVehicleReport,
  selectVehicleReportStatus,
} from "../../../features/reports/reportsSlice";
import {
  CollapsableItemList,
  Container,
  ContainerLineItem,
  Icon,
  IconList,
  Layout,
  LoadingPlaceholder,
  Padding,
  Text,
} from "@kidslivesafe/sc-react";

import {
  VehicleReportType,
  Option,
} from "../../../features/reports/reportTypes";
import LoadingContainerLineItem from "../../../components/UIComponents/LoadingContainerLineItem/LoadingContainerLineItem";
import { generateList } from "@kidslivesafe/sc-foundation";
import { formatNumberCommas } from "../../../utils";

const InstalledOptionsAndPackages: React.FC<{
  containerId: string;
  printReady?: boolean;
}> = ({ containerId, printReady }) => {
  const [optionsAndPackages, setOptionsAndPackages] = React.useState<Option[]>(
    [],
  );

  let pulledReport = useAppSelector(selectVehicleReport) as VehicleReportType;
  const vehicleReportStatus = useAppSelector(selectVehicleReportStatus);

  const loading = vehicleReportStatus === "loading";

  useEffect(() => {
    if (
      pulledReport &&
      pulledReport.features &&
      pulledReport.features.installedOptionsAndPackages
    ) {
      setOptionsAndPackages(pulledReport.features.installedOptionsAndPackages);
    }
  }, [pulledReport]);

  return (
    <Padding
      space="none"
      breakpointStyles={{
        mobile: {
          left: "md",
          right: "md",
        },
      }}
    >
      <Container
        containerId={containerId}
        containerTitle={`Installed Options & Packages ${
          loading
            ? ""
            : `(${optionsAndPackages ? optionsAndPackages.length : 0})`
        }`}
      >
        {loading ||
        (pulledReport &&
          pulledReport.features &&
          pulledReport.features.installedOptionsAndPackages &&
          pulledReport.features.installedOptionsAndPackages.length > 0) ? (
          <Container altColor="grey">
            <Padding space="md">
              <Layout
                type="flex"
                flexProps={{ gap: "xs", direction: "column" }}
              >
                <Layout
                  type="flex"
                  flexProps={{ gap: "xxs", alignItems: "center" }}
                >
                  <Text size="sm" textWrap="wrap" tag="div">
                    <Icon name={IconList.IconDiamond} size={18} />
                  </Text>
                  <Text tag="div" size="xs">
                    indicates a high value feature that may improve resale
                    value.
                  </Text>
                </Layout>
                <Layout
                  type="flex"
                  flexProps={{ gap: "xxs", alignItems: "center" }}
                >
                  {loading ? (
                    <LoadingPlaceholder width="50%" height="10px" noGrow />
                  ) : (
                    <ContainerLineItem
                      noPadding={true}
                      key="installed-options-and-packages-header"
                      lineItemHeader={
                        <Layout
                          type="flex"
                          flexProps={{ gap: "xs", alignItems: "center" }}
                        >
                          <Text>Combined Total MSRP</Text>
                        </Layout>
                      }
                      lineItemSubHeader={formatNumberCommas(
                        pulledReport.vehicleDetails?.installedOptionsMsrp ||
                          "0",
                        true,
                      )}
                    />
                  )}
                </Layout>
              </Layout>
            </Padding>
          </Container>
        ) : null}
        {loading ? (
          <LoadingContainerLineItem
            lineItemHeader
            lineItemSubHeader
            numberToLoad={3}
          />
        ) : pulledReport &&
          pulledReport.features &&
          pulledReport.features.installedOptionsAndPackages &&
          pulledReport.features.installedOptionsAndPackages.length > 0 ? (
          <CollapsableItemList
            numberToShow={5}
            isOpen={printReady ? true : false}
          >
            {optionsAndPackages.map((optionItem, index) => (
              <ContainerLineItem
                key={index}
                lineItemHeader={
                  <>
                    <Layout
                      type="flex"
                      flexProps={{
                        gap: "xs",
                        alignItems: "center",
                      }}
                    >
                      <Text tag="div" color="success">
                        <Icon
                          name={
                            optionItem.isHighValueFeature
                              ? IconList.IconDiamond
                              : IconList.IconCircleCheck
                          }
                          size={20}
                        />
                      </Text>
                      <Layout
                        type="flex"
                        flexProps={{
                          alignItems: "flex-start",
                          direction: "column",
                        }}
                      >
                        <Text tag="div">
                          {optionItem.name}{" "}
                          {optionItem.code ? "(" + optionItem.code + ")" : null}
                        </Text>
                        <Text tag="div" size="xs" fontWeight="normal">
                          {generateList(
                            [
                              {
                                value: optionItem.msrp
                                  ? formatNumberCommas(optionItem.msrp, true)
                                  : "",
                                label: " MSRP",
                                order: "reverse",
                              },
                            ],
                            "/",
                          )}
                        </Text>
                      </Layout>
                    </Layout>
                  </>
                }
              />
            ))}
          </CollapsableItemList>
        ) : (
          <Padding space="md">
            <Text size="sm" fontWeight="bold">
              No installed options & packages were found on this vehicle.
            </Text>
          </Padding>
        )}
      </Container>
    </Padding>
  );
};

export default InstalledOptionsAndPackages;
